/* mw-announcement-bar — the Announcement bar section (sections/announcement-bar.liquid).
   Light DOM, no dependencies. Styles: assets/section-announcement.css.

   Two types, both measured on Helix (helix-theme-shoes.myshopify.com):

   - «Slider». The messages share one grid cell and cross-fade: the one leaving fades out over
     250 ms while it rises 10 px (cubic-bezier(.55,.055,.675,.19)), then the one arriving fades in
     over 400 ms from 10 px below (cubic-bezier(.215,.61,.355,1)) — Helix' reveal-carousel timings
     to the millisecond. Nothing changes size, so a message change costs no layout shift.
   - «Scrolling». The row is copied until it covers the bar and the track is moved by exactly one
     copy per loop, so the seam never shows. The pace is constant pixels per second, from Helix'
     own marquee curve (20.5 px/s at speed 0.1 → 310 px/s at speed 2; the default speed 1 is
     157.6 px/s), so a phone runs as fast as a desktop.

   Pausing (WCAG 2.2.2 — Helix has no stop, we keep ours): the visible stop, hover when the
   merchant asked for it, keyboard focus inside the bar, a block selected in the theme editor,
   the bar off screen, a hidden tab. Reduced motion stops both types completely: no rotation, no
   marquee — the arrows still work and the scrolling row becomes a row that can be scrolled by
   hand. Without JavaScript both types are a swipeable row of all the messages.

   A Free shipping progress block is re-rendered from the server after every cart change
   (cart:updated), so the amount is always Liquid's, never money formatted in JavaScript. */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* Helix' marquee speed curve, measured from its own script and confirmed against the demo
   (speed 0.5 → 81.45 px/s on a 2570 px row = 31.55 s). */
const MIN_PX_PER_SECOND = 20.5;
const MAX_PX_PER_SECOND = 310;
const MIN_SPEED = 0.1;
const MAX_SPEED = 2;

const OUT_MS = 250;
const IN_MS = 400;
const OUT_EASE = 'cubic-bezier(0.55, 0.055, 0.675, 0.19)';
const IN_EASE = 'cubic-bezier(0.215, 0.61, 0.355, 1)';
const NUDGE = 10;
const SWIPE_MIN = 40;

class MwAnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.inner = this.querySelector(':scope > .announcement-bar__inner');
    this.list = this.inner?.querySelector(':scope > .announcement-bar__list');
    this.track = this.list?.querySelector(':scope > .announcement-bar__track');
    this.group = this.track?.querySelector(':scope > .announcement-bar__group');
    if (!this.group) return;

    this.type = this.dataset.type === 'scrolling' ? 'scrolling' : 'slider';
    this.items = this.readItems();
    if (!this.items.length) return;

    this.pauses = new Set();
    this.abort = new AbortController();
    const { signal } = this.abort;

    if (this.hasAttribute('data-pause-on-hover')) {
      this.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && this.pause('hover'), { signal });
      this.addEventListener('pointerleave', (event) => event.pointerType === 'mouse' && this.resume('hover'), { signal });
    }
    /* Focus on a message or an arrow holds the bar still while it is there — the bar has no
       visible stop, so this and «Pause on hover» are what a keyboard or mouse user gets. */
    this.addEventListener('focusin', () => this.pause('focus'), { signal });
    this.addEventListener(
      'focusout',
      (event) => {
        if (!(event.relatedTarget instanceof Node) || !this.contains(event.relatedTarget)) this.resume('focus');
      },
      { signal }
    );
    document.addEventListener(
      'visibilitychange',
      () => (document.hidden ? this.pause('hidden') : this.resume('hidden')),
      { signal }
    );

    if ('IntersectionObserver' in window) {
      this.visibility = new IntersectionObserver(
        ([entry]) => (entry.isIntersecting ? this.resume('offscreen') : this.pause('offscreen')),
        { rootMargin: '100px 0px' }
      );
      this.visibility.observe(this);
    }

    if (window.Shopify?.designMode) {
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          this.pause('editor');
          const item = event.target instanceof Element ? event.target.closest('.announcement-bar__item') : null;
          const index = item ? this.items.indexOf(item) : -1;
          if (index >= 0 && this.type === 'slider') this.goTo(index, { instant: true });
        },
        { signal }
      );
      this.addEventListener('shopify:block:deselect', () => this.resume('editor'), { signal });
    }

    this.onMotionChange = () => this.applyMotion();
    reducedMotion.addEventListener('change', this.onMotionChange, { signal });

    if (this.type === 'slider') this.setUpSlider(signal);
    else this.setUpScrolling();

    if (this.querySelector('[data-free-shipping]')) {
      document.addEventListener('cart:updated', this.onCartUpdate, { signal });
    }

    this.renderPause();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.visibility?.disconnect();
    this.resizeObserver?.disconnect();
    clearInterval(this.timer);
    cancelAnimationFrame(this.frame);
  }

  /* Defensive: with "tag": null Shopify adds no wrapper, but a future Shopify could wrap each
     block in <div class="shopify-block"> (docs/17 §2) — then the item is one level down. */
  readItems() {
    return [...this.group.children].flatMap((child) => {
      if (child.classList.contains('announcement-bar__item')) return [child];
      const nested = child.querySelector(':scope > .announcement-bar__item');
      return nested ? [nested] : [];
    });
  }

  /* ---------- Slider ---------- */

  setUpSlider(signal) {
    this.index = Math.max(
      0,
      this.items.findIndex((item) => item.classList.contains('is-active'))
    );
    this.select(this.index);
    this.classList.add('is-ready');

    this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.step(-1), { signal });
    this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.step(1), { signal });

    if (this.items.length > 1) {
      this.list.addEventListener(
        'pointerdown',
        (event) => {
          if (event.pointerType === 'mouse') return;
          this.swipeX = event.clientX;
        },
        { passive: true, signal }
      );
      this.list.addEventListener(
        'pointerup',
        (event) => {
          if (this.swipeX == null) return;
          const delta = event.clientX - this.swipeX;
          this.swipeX = null;
          if (Math.abs(delta) >= SWIPE_MIN) this.step(delta < 0 ? 1 : -1);
        },
        { passive: true, signal }
      );
    }

    const seconds = Number.parseFloat(this.dataset.autoplay);
    this.delay = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0;
    this.applyMotion();
  }

  /* An arrow or a swipe is a deliberate choice: the rotation stops and the visible stop switches
     to «play», so it is obvious how to start it again. */
  step(direction) {
    this.goTo(this.index + direction);
    this.pause('user');
  }

  select(index) {
    this.index = index;
    this.items.forEach((item, i) => {
      const active = i === index;
      item.classList.toggle('is-active', active);
      item.inert = !active;
    });
  }

  async goTo(next, { instant = false } = {}) {
    const total = this.items.length;
    if (total < 2) return;
    const target = ((next % total) + total) % total;
    if (target === this.index) return;

    if (this.animating) {
      this.queued = target;
      return;
    }

    const from = this.items[this.index];
    const to = this.items[target];

    if (instant || reducedMotion.matches || typeof from.animate !== 'function') {
      this.select(target);
      return;
    }

    this.animating = true;
    try {
      await this.play(from, { opacity: [1, 0], transform: ['translateY(0)', `translateY(-${NUDGE}px)`] }, OUT_MS, OUT_EASE);
      this.select(target);
      await this.play(to, { opacity: [0, 1], transform: [`translateY(${NUDGE}px)`, 'translateY(0)'] }, IN_MS, IN_EASE);
    } catch {
      this.select(target);
    } finally {
      this.animating = false;
      const queued = this.queued;
      this.queued = null;
      if (queued != null && queued !== this.index) this.goTo(queued);
    }
  }

  /* The animation is cancelled once it has finished: the class decides the resting state, so
     nothing is left committed on the element. */
  async play(element, keyframes, duration, easing) {
    const animation = element.animate(keyframes, { duration, easing, fill: 'forwards' });
    try {
      await animation.finished;
    } finally {
      animation.cancel();
    }
  }

  /* ---------- Scrolling ---------- */

  setUpScrolling() {
    this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
    this.resizeObserver.observe(this.list);
    this.resizeObserver.observe(this.group);
    this.applyMotion();
  }

  scheduleUpdate() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.updateScrolling());
  }

  clones() {
    return [...this.track.children].filter((el) => el !== this.group);
  }

  updateScrolling() {
    const groupWidth = this.group.getBoundingClientRect().width;
    const listWidth = this.list.clientWidth;

    if (reducedMotion.matches || !groupWidth || !listWidth) {
      this.clones().forEach((el) => el.remove());
      this.classList.remove('is-ready');
      return;
    }

    const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const shift = groupWidth + gap;
    const wanted = Math.max(1, Math.ceil(listWidth / shift));
    const clones = this.clones();
    for (let i = clones.length; i < wanted; i += 1) this.track.append(this.makeClone());
    clones.slice(wanted).forEach((el) => el.remove());

    this.style.setProperty('--ann-shift', `${shift.toFixed(2)}px`);
    this.style.setProperty('--ann-duration', `${Math.max(2, Math.min(600, shift / this.pixelsPerSecond())).toFixed(3)}s`);
    this.classList.add('is-ready');
  }

  /* Helix' curve, so «Speed 1» here is «Speed 1» there. */
  pixelsPerSecond() {
    const speed = Number.parseFloat(this.dataset.speed);
    const value = Number.isFinite(speed) ? speed : 1;
    const normalised = Math.max(0, Math.min(1, (value - MIN_SPEED) / (MAX_SPEED - MIN_SPEED)));
    return MIN_PX_PER_SECOND + (MAX_PX_PER_SECOND - MIN_PX_PER_SECOND) * normalised;
  }

  /* The copies are decoration: screen readers and the keyboard meet every message once. */
  makeClone() {
    const clone = this.group.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.classList.add('announcement-bar__group--clone');
    [clone, ...clone.querySelectorAll('[id], [data-shopify-editor-block]')].forEach((el) => {
      el.removeAttribute('id');
      el.removeAttribute('data-shopify-editor-block');
    });
    return clone;
  }

  /* ---------- Pausing ---------- */

  pause(reason) {
    this.pauses.add(reason);
    this.renderPause();
  }

  resume(reason) {
    this.pauses.delete(reason);
    this.renderPause();
  }

  renderPause() {
    const held = this.pauses.size > 0;
    this.classList.toggle('is-paused', held);
    if (this.type === 'slider') this.applyAutoplay();
  }

  applyAutoplay() {
    clearInterval(this.timer);
    if (!this.delay || this.pauses.size > 0 || reducedMotion.matches || this.items.length < 2) return;
    this.timer = setInterval(() => this.goTo(this.index + 1), this.delay);
  }

  applyMotion() {
    if (this.type === 'slider') this.applyAutoplay();
    else this.scheduleUpdate();
  }

  /* ---------- Free shipping ---------- */

  onCartUpdate = async () => {
    const id = this.dataset.sectionId;
    const targets = [...this.querySelectorAll('[data-free-shipping] .free-shipping__message')];
    if (!id || !targets.length) return;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}?section_id=${encodeURIComponent(id)}`);
      if (!response.ok) return;
      const fresh = new DOMParser()
        .parseFromString(await response.text(), 'text/html')
        .querySelector('[data-free-shipping] .free-shipping__message');
      if (!fresh) return;
      targets.forEach((target) => {
        target.innerHTML = fresh.innerHTML;
      });
    } catch {
      /* The bar keeps the amount it was rendered with; nothing on the page breaks. */
    }
  };
}

if (!customElements.get('mw-announcement-bar')) customElements.define('mw-announcement-bar', MwAnnouncementBar);

export { MwAnnouncementBar };
